import { getAdminSessionEntryUrl } from '@/utils/authRedirect';
import { logError } from '@/composables';
import { showToast } from '@/composables/swal';
import { ApiResponse } from '@/models';
import { attachCsrfMutatingInterceptor } from '@/utils/csrf';
import Axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';

/**
 * Mirrors Builder V2 (`development_custimoo_builder_v2/src/services/api.ts`):
 * - `skipGlobalAuthErrorHandling` � refresh / endpoints that must not trigger logout retry loops
 * - `_retry` � single retry after refresh (no infinite 401 recursion)
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipGlobalAuthErrorHandling?: boolean;
    _retry?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    skipGlobalAuthErrorHandling?: boolean;
    _retry?: boolean;
  }
}

const base_url = import.meta.env.VITE_APP_BASE_URL;

function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(base64)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Normalize tokens from refresh/login payloads (shape differs slightly across endpoints). */
function extractTokensFromRefreshResult(result: unknown): {
  access_token?: string;
  refresh_token?: string;
} {
  if (!result || typeof result !== 'object') return {};
  const r = result as Record<string, unknown>;
  const user = r.user as Record<string, unknown> | undefined;
  const pick = (v: unknown): string | undefined =>
    typeof v === 'string' && v.length > 0 ? v : undefined;

  return {
    access_token:
      pick(r.access_token) ??
      pick(r.accessToken) ??
      (user ? pick(user.access_token) ?? pick(user.accessToken) : undefined),
    refresh_token:
      pick(r.refresh_token) ??
      pick(r.refreshToken) ??
      (user ? pick(user.refresh_token) ?? pick(user.refreshToken) : undefined),
  };
}

/** Access JWT exp within skewSeconds (proactive refresh, same idea as Builder token expiry check). */
function accessExpiredOrSoon(token: string | null, skewSeconds: number): boolean {
  if (!token) return false;
  const exp = parseJwtExp(token);
  if (exp === null) return false;
  return exp * 1000 <= Date.now() + skewSeconds * 1000;
}

let refreshPromise: Promise<boolean> | null = null;

function applySessionFromPayload(user: {
  access_token?: string;
  refresh_token?: string;
}): void {
  if (user.access_token) localStorage.setItem('access_token', user.access_token);
  if (user.refresh_token) localStorage.setItem('refresh_token', user.refresh_token);
}

/** Single in-flight refresh shared by proactive (request) and reactive (401) paths � avoids duplicate POSTs. */
function scheduleRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function refreshTokens(): Promise<boolean> {
  const rt = localStorage.getItem('refresh_token');
  if (!rt) return false;
  try {
    const res = await noTokenRequest.post<{
      success?: boolean;
      result?: unknown;
    }>('refresh-token', { refresh_token: rt }, { skipGlobalAuthErrorHandling: true });
    const data = res.data;
    if (!data.success || data.result === undefined || data.result === null) return false;
    const extracted = extractTokensFromRefreshResult(data.result);
    applySessionFromPayload(extracted);
    const ok = !!localStorage.getItem('access_token');
    if (!ok && import.meta.env.DEV) {
      console.warn('[Admin V2] refresh-token parsed but access_token missing', {
        keys: data.result && typeof data.result === 'object' ? Object.keys(data.result as object) : [],
      });
    }
    return ok;
  } catch (e: unknown) {
    const ax = e as { response?: { status?: number; data?: unknown }; message?: string };
    console.error('[Admin V2] POST refresh-token failed', {
      status: ax.response?.status,
      body: ax.response?.data,
      apiBase: base_url,
      hint:
        'Staging: verify VITE_APP_BASE_URL matches the API, CORS allows this origin, SECRET_KEY matches login server.',
    });
    return false;
  }
}

const http: AxiosInstance = Axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  headers: {
    'Content-type': 'application/json',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Identifies this app to the backend so techpack-PDF regeneration can be attributed to the
    // originating platform (only this platform shows a "generating" state for its own requests).
    'X-Platform': 'admin_v2',
  },
});

const noTokenRequest: AxiosInstance = Axios.create({
  baseURL: base_url,
  headers: {
    'Content-type': 'application/json',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'X-Platform': 'admin_v2',
  },
});

http.interceptors.request.use(async (config) => {
  const url = config.url ?? '';
  if (url.includes('refresh-token')) {
    return config;
  }

  let token = localStorage.getItem('access_token');
  const refreshTok = localStorage.getItem('refresh_token');

  if (token && refreshTok && accessExpiredOrSoon(token, 60)) {
    await scheduleRefresh();
    token = localStorage.getItem('access_token');
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const sessionEntryUrl = () => getAdminSessionEntryUrl();

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.config?.skipGlobalAuthErrorHandling) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const original = error.config as InternalAxiosRequestConfig | undefined;

    if (!original || status !== 401) {
      return Promise.reject(error);
    }

    if (original._retry) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = sessionEntryUrl();
      return Promise.reject(error);
    }

    const refreshed = await scheduleRefresh();
    if (!refreshed) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = sessionEntryUrl();
      return Promise.reject(error);
    }

    original._retry = true;
    original.headers = AxiosHeaders.from(original.headers ?? {});
    original.headers.set(
      'Authorization',
      `Bearer ${localStorage.getItem('access_token')}`
    );
    return http.request(original);
  }
);

attachCsrfMutatingInterceptor(http);
attachCsrfMutatingInterceptor(noTokenRequest);

const handleResponseException = (
  errorResponse: AxiosError<ApiResponse<null>> | TypeError
): void => {
  let message: string | undefined = '';
  if ('isAxiosError' in errorResponse) {
    message = errorResponse.response?.data?.message;
    if (!message) {
      message = errorResponse.response?.statusText;
    }
    showToast(message, 'error');
    logError('Error (Axios): ', message);
  } else {
    message = errorResponse.message;
    showToast(message, 'error');
    logError(`Error (${errorResponse.name}): `, {
      name: errorResponse.name,
      message: errorResponse.message,
      stack: errorResponse.stack,
    });
  }
  logError('Error (Axios): ', message);
};

export { handleResponseException, http, noTokenRequest };
